import logger from '@utils/logger'
/**
 * Performance monitoring utilities for Core Web Vitals
 * Measures and reports key performance metrics
 */

// Core Web Vitals thresholds (in milliseconds)
const THRESHOLDS = {
  LCP: 2500, // Largest Contentful Paint
  FID: 100,  // First Input Delay
  CLS: 0.1   // Cumulative Layout Shift
}

/**
 * Measure Largest Contentful Paint (LCP)
 * @param {Function} callback - Function to call with LCP value
 */
export function measureLCP(callback) {
  if (!('PerformanceObserver' in window)) return

  const observer = new PerformanceObserver((list) => {
    const entries = list.getEntries()
    const lastEntry = entries[entries.length - 1]
    
    if (lastEntry) {
      const lcp = lastEntry.startTime
      callback({
        name: 'LCP',
        value: lcp,
        rating: lcp <= THRESHOLDS.LCP ? 'good' : lcp <= 4000 ? 'needs-improvement' : 'poor'
      })
    }
  })

  try {
    observer.observe({ entryTypes: ['largest-contentful-paint'] })
  } catch (e) {
    logger.warn('LCP measurement not supported')
  }
}

/**
 * Measure First Input Delay (FID)
 * @param {Function} callback - Function to call with FID value
 */
export function measureFID(callback) {
  if (!('PerformanceObserver' in window)) return

  const observer = new PerformanceObserver((list) => {
    const entries = list.getEntries()
    
    entries.forEach((entry) => {
      const fid = entry.processingStart - entry.startTime
      callback({
        name: 'FID',
        value: fid,
        rating: fid <= THRESHOLDS.FID ? 'good' : fid <= 300 ? 'needs-improvement' : 'poor'
      })
    })
  })

  try {
    observer.observe({ entryTypes: ['first-input'] })
  } catch (e) {
    logger.warn('FID measurement not supported')
  }
}

/**
 * Measure Cumulative Layout Shift (CLS)
 * @param {Function} callback - Function to call with CLS value
 */
export function measureCLS(callback) {
  if (!('PerformanceObserver' in window)) return

  let clsValue = 0
  let sessionValue = 0
  let sessionEntries = []

  const observer = new PerformanceObserver((list) => {
    const entries = list.getEntries()
    
    entries.forEach((entry) => {
      if (!entry.hadRecentInput) {
        const firstSessionEntry = sessionEntries[0]
        const lastSessionEntry = sessionEntries[sessionEntries.length - 1]

        if (sessionValue && 
            entry.startTime - lastSessionEntry.startTime < 1000 &&
            entry.startTime - firstSessionEntry.startTime < 5000) {
          sessionValue += entry.value
          sessionEntries.push(entry)
        } else {
          sessionValue = entry.value
          sessionEntries = [entry]
        }

        if (sessionValue > clsValue) {
          clsValue = sessionValue
          callback({
            name: 'CLS',
            value: clsValue,
            rating: clsValue <= THRESHOLDS.CLS ? 'good' : clsValue <= 0.25 ? 'needs-improvement' : 'poor'
          })
        }
      }
    })
  })

  try {
    observer.observe({ entryTypes: ['layout-shift'] })
  } catch (e) {
    logger.warn('CLS measurement not supported')
  }
}

/**
 * Initialize all Core Web Vitals measurements
 * @param {Function} onMetric - Callback function for each metric
 */
export function initPerformanceMonitoring(onMetric = null) {
  const callback = onMetric || (() => {})
  
  // Measure all Core Web Vitals
  measureLCP(callback)
  measureFID(callback)
  measureCLS(callback)

  // Additional performance metrics
  measureNavigationTiming(callback)
  measureResourceTiming(callback)
}

/**
 * Measure navigation timing metrics
 * @param {Function} callback - Function to call with timing data
 */
function measureNavigationTiming(callback) {
  if (!('performance' in window) || !performance.getEntriesByType) return

  window.addEventListener('load', () => {
    const navigation = performance.getEntriesByType('navigation')[0]
    
    if (navigation) {
      const metrics = {
        name: 'Navigation',
        domContentLoaded: navigation.domContentLoadedEventEnd - navigation.domContentLoadedEventStart,
        loadComplete: navigation.loadEventEnd - navigation.loadEventStart,
        domInteractive: navigation.domInteractive - navigation.fetchStart,
        ttfb: navigation.responseStart - navigation.requestStart // Time to First Byte
      }
      
      callback(metrics)
    }
  })
}

/**
 * Measure resource loading performance
 * @param {Function} callback - Function to call with resource data
 */
function measureResourceTiming(callback) {
  if (!('performance' in window) || !performance.getEntriesByType) return

  window.addEventListener('load', () => {
    const resources = performance.getEntriesByType('resource')
    
    const slowResources = resources
      .filter(resource => resource.duration > 1000) // Resources taking more than 1s
      .map(resource => ({
        name: resource.name,
        duration: resource.duration,
        size: resource.transferSize || 0,
        type: resource.initiatorType
      }))

    if (slowResources.length > 0) {
      callback({
        name: 'SlowResources',
        resources: slowResources
      })
    }
  })
}

/**
 * Optimize images for lazy loading
 * @param {HTMLImageElement} img - Image element to optimize
 */
export function optimizeImage(img) {
  if (!img || img.tagName !== 'IMG') return

  // Add loading="lazy" for native lazy loading
  img.loading = 'lazy'
  
  // Add decoding="async" for better performance
  img.decoding = 'async'
  
  // Add fetchpriority for important images
  if (img.classList.contains('hero-image') || img.classList.contains('above-fold')) {
    img.fetchPriority = 'high'
  }
}

/**
 * Debounce function for performance optimization
 * @param {Function} func - Function to debounce
 * @param {number} wait - Wait time in milliseconds
 * @returns {Function} Debounced function
 */
export function debounce(func, wait) {
  let timeout
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout)
      func(...args)
    }
    clearTimeout(timeout)
    timeout = setTimeout(later, wait)
  }
}

/**
 * Throttle function for performance optimization
 * @param {Function} func - Function to throttle
 * @param {number} limit - Time limit in milliseconds
 * @returns {Function} Throttled function
 */
export function throttle(func, limit) {
  let inThrottle
  return function executedFunction(...args) {
    if (!inThrottle) {
      func.apply(this, args)
      inThrottle = true
      setTimeout(() => inThrottle = false, limit)
    }
  }
}

/**
 * Check if user prefers reduced motion
 * @returns {boolean} True if user prefers reduced motion
 */
export function prefersReducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

/**
 * Get device performance tier based on hardware capabilities
 * @returns {string} Performance tier: 'high', 'medium', or 'low'
 */
export function getDevicePerformanceTier() {
  // Check for hardware concurrency (CPU cores)
  const cores = navigator.hardwareConcurrency || 1
  
  // Check for device memory (if available)
  const memory = navigator.deviceMemory || 1
  
  // Check for connection speed
  const connection = navigator.connection
  const effectiveType = connection?.effectiveType || '4g'
  
  // Determine performance tier
  if (cores >= 8 && memory >= 8 && effectiveType === '4g') {
    return 'high'
  } else if (cores >= 4 && memory >= 4) {
    return 'medium'
  } else {
    return 'low'
  }
}

/**
 * Preload critical resources
 * @param {Array} resources - Array of resource URLs to preload
 */
export function preloadCriticalResources(resources = []) {
  resources.forEach(resource => {
    const link = document.createElement('link')
    link.rel = 'preload'
    link.href = resource.url
    link.as = resource.as || 'fetch'
    if (resource.crossorigin) link.crossOrigin = resource.crossorigin
    document.head.appendChild(link)
  })
}

/**
 * Optimize scroll performance with passive event listeners
 */
export function optimizeScrollPerformance() {
  // Add passive event listeners for better scroll performance
  
  // Override addEventListener for scroll events to use passive listeners
  const originalAddEventListener = EventTarget.prototype.addEventListener
  EventTarget.prototype.addEventListener = function(type, listener, options) {
    if (type === 'scroll' || type === 'touchstart' || type === 'touchmove') {
      options = typeof options === 'boolean' ? { passive: true, capture: options } : { ...options, passive: true }
    }
    return originalAddEventListener.call(this, type, listener, options)
  }
}

/**
 * Implement intersection observer for lazy loading
 * @param {string} selector - CSS selector for elements to observe
 * @param {Function} callback - Callback function when element enters viewport
 * @param {Object} options - Intersection observer options
 */
export function createIntersectionObserver(selector, callback, options = {}) {
  if (!('IntersectionObserver' in window)) {
    // Fallback for browsers without IntersectionObserver
    document.querySelectorAll(selector).forEach(callback)
    return null
  }

  const defaultOptions = {
    root: null,
    rootMargin: '50px',
    threshold: 0.1,
    ...options
  }

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        callback(entry.target)
        observer.unobserve(entry.target)
      }
    })
  }, defaultOptions)

  document.querySelectorAll(selector).forEach((el) => {
    observer.observe(el)
  })

  return observer
}

/**
 * Optimize component re-renders with shallow comparison
 * @param {Object} prevProps - Previous props
 * @param {Object} nextProps - Next props
 * @returns {boolean} True if props are equal (should not re-render)
 */
export function shallowEqual(prevProps, nextProps) {
  const prevKeys = Object.keys(prevProps)
  const nextKeys = Object.keys(nextProps)

  if (prevKeys.length !== nextKeys.length) {
    return false
  }

  for (let key of prevKeys) {
    if (prevProps[key] !== nextProps[key]) {
      return false
    }
  }

  return true
}

/**
 * Create a performance-optimized event handler
 * @param {Function} handler - Event handler function
 * @param {number} delay - Debounce delay in milliseconds
 * @param {boolean} useThrottle - Use throttle instead of debounce
 * @returns {Function} Optimized event handler
 */
export function createOptimizedHandler(handler, delay = 100, useThrottle = false) {
  return useThrottle ? throttle(handler, delay) : debounce(handler, delay)
}

/**
 * Measure and report component render performance
 * @param {string} componentName - Name of the component
 * @param {Function} renderFunction - Function that renders the component
 * @returns {*} Result of the render function
 */
export function measureRenderPerformance(componentName, renderFunction) {
  const startTime = performance.now()
  const result = renderFunction()
  const endTime = performance.now()
  
  const renderTime = endTime - startTime
  
  // Log performance warning if render takes too long
  if (renderTime > 16) { // 16ms = 60fps threshold
    logger.warn(`${componentName} render took ${renderTime.toFixed(2)}ms (>16ms threshold)`)
  }
  
  return result
}

/**
 * Optimize images with modern formats and lazy loading
 * @param {HTMLImageElement} img - Image element to optimize
 * @param {Object} options - Optimization options
 */
export function optimizeImageAdvanced(img, options = {}) {
  if (!img || img.tagName !== 'IMG') return

  const {
    enableLazyLoading = true,
  } = options

  // Add loading="lazy" for native lazy loading
  if (enableLazyLoading) {
    img.loading = 'lazy'
  }
  
  // Add decoding="async" for better performance
  img.decoding = 'async'
  
  // Add fetchpriority for important images
  if (img.classList.contains('hero-image') || img.classList.contains('above-fold')) {
    img.fetchPriority = 'high'
  } else {
    img.fetchPriority = 'low'
  }

  // Add error handling
  img.onerror = () => {
    logger.warn(`Failed to load image: ${img.src}`)
  }

  // Add intersection observer for advanced lazy loading
  if (enableLazyLoading && 'IntersectionObserver' in window) {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const img = entry.target
          if (img.dataset.src) {
            img.src = img.dataset.src
            img.removeAttribute('data-src')
          }
          observer.unobserve(img)
        }
      })
    }, { rootMargin: '50px' })

    observer.observe(img)
  }
}

/**
 * Initialize all performance optimizations
 */
export function initAllPerformanceOptimizations() {
  // Optimize scroll performance
  optimizeScrollPerformance()
  
  // Optimize all images on the page
  document.querySelectorAll('img').forEach(img => {
    optimizeImageAdvanced(img)
  })
}
